import { cn } from '@/lib/utils';

/**
 * Animated School Notebook Loader.
 *
 * Recreates the exact user-provided design states:
 * 1. Open State: Dark blue cover on left, cream ruled paper page on right with red margin line & text, pencil writing.
 * 2. Closed State: Closed dark blue notebook with "NOTEBOOK" label badge, pencil resting on top.
 */

const SIZES = {
  sm: 'h-5 w-5',
  md: 'h-9 w-9',
  lg: 'h-14 w-14',
  xl: 'h-20 w-20',
} as const;

export interface LoaderProps {
  size?: keyof typeof SIZES;
  className?: string;
}

export function Loader({ size = 'md', className }: LoaderProps) {
  return (
    <div
      role="status"
      className={cn(
        'inline-flex items-center justify-center shrink-0 relative select-none',
        SIZES[size],
        className,
      )}
      aria-hidden
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full overflow-visible"
      >
        <defs>
          <style>{`
            @keyframes openBookFade {
              0%, 42% { opacity: 1; transform: scale(1); }
              52%, 90% { opacity: 0; transform: scale(0.96); }
              96%, 100% { opacity: 1; transform: scale(1); }
            }
            @keyframes closedBookFade {
              0%, 42% { opacity: 0; transform: scale(0.96); }
              52%, 90% { opacity: 1; transform: scale(1); }
              96%, 100% { opacity: 0; transform: scale(0.96); }
            }
            @keyframes pageTurnFlip {
              0%, 40% { transform: rotateY(0deg); opacity: 0; }
              44% { opacity: 1; }
              54% { transform: rotateY(-180deg); opacity: 1; }
              58%, 100% { transform: rotateY(-180deg); opacity: 0; }
            }
            @keyframes pencilStoryMotion {
              /* 0% - 40%: Writing on open notebook right page */
              0% { transform: translate(0px, 0px) rotate(0deg); }
              10% { transform: translate(-3px, 4px) rotate(4deg); }
              20% { transform: translate(4px, 8px) rotate(-3deg); }
              30% { transform: translate(-2px, 12px) rotate(4deg); }
              40% { transform: translate(0px, 0px) rotate(0deg); }
              
              /* 40% - 55%: Lifting & moving to center of closed cover */
              52% { transform: translate(-22px, -8px) rotate(-18deg); }
              
              /* 55% - 90%: Resting on top of closed NOTEBOOK label badge */
              60%, 88% { transform: translate(-24px, 4px) rotate(-22deg); }
              
              /* 90% - 100%: Smoothly returning to open page writing stance */
              100% { transform: translate(0px, 0px) rotate(0deg); }
            }
            @keyframes inkLineWrite {
              0% { stroke-dashoffset: 40; opacity: 0.3; }
              38% { stroke-dashoffset: 0; opacity: 1; }
              42%, 100% { stroke-dashoffset: -40; opacity: 0; }
            }
            .animate-open-book {
              animation: openBookFade 4.5s ease-in-out infinite;
              transform-origin: center;
            }
            .animate-closed-book {
              animation: closedBookFade 4.5s ease-in-out infinite;
              transform-origin: center;
            }
            .animate-page-turn {
              animation: pageTurnFlip 4.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
              transform-origin: 48px 48px;
            }
            .animate-pencil-story {
              animation: pencilStoryMotion 4.5s cubic-bezier(0.45, 0, 0.55, 1) infinite;
              transform-origin: 68px 52px;
            }
            .animate-ink-trace {
              stroke-dasharray: 8 4;
              animation: inkLineWrite 4.5s linear infinite;
            }
          `}</style>

          {/* Navy Blue Cover Gradient */}
          <linearGradient id="navyCoverGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1e40af" />
            <stop offset="100%" stopColor="#172554" />
          </linearGradient>

          {/* Yellow Pencil 3D Gradient */}
          <linearGradient id="pencilYellowBodyGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fde047" />
            <stop offset="40%" stopColor="#eab308" />
            <stop offset="100%" stopColor="#ca8a04" />
          </linearGradient>
        </defs>

        {/* ---------------- 1. OPEN STATE (Matches Image 2) ---------------- */}
        <g className="animate-open-book">
          {/* Open Book Shadow */}
          <path d="M 5 18 L 95 14 L 95 82 L 5 86 Z" fill="#020617" opacity="0.1" />

          {/* Left Open Cover (Solid Dark Royal Blue with Rounded Left Corners) */}
          <rect x="6" y="18" width="42" height="66" rx="4" fill="url(#navyCoverGrad)" />

          {/* Right Open Page (Clean Ruled Cream Sheet with Rounded Right Corners) */}
          <rect x="48" y="16" width="46" height="66" rx="4" fill="#fefdf8" stroke="#cbd5e1" strokeWidth="0.5" />

          {/* Red Vertical Margin Line */}
          <line x1="56" y1="16" x2="56" y2="82" stroke="#ef4444" strokeWidth="0.8" opacity="0.75" />

          {/* Blue Horizontal Rule Lines */}
          <line x1="48" y1="23" x2="94" y2="23" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="30" x2="94" y2="30" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="37" x2="94" y2="37" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="44" x2="94" y2="44" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="51" x2="94" y2="51" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="58" x2="94" y2="58" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="65" x2="94" y2="65" stroke="#bfdbfe" strokeWidth="0.75" />
          <line x1="48" y1="72" x2="94" y2="72" stroke="#bfdbfe" strokeWidth="0.75" />

          {/* Handwritten Ink Lines (Matching Image 2) */}
          <path d="M 58 28 Q 72 27, 86 28" stroke="#334155" strokeWidth="1.2" strokeLinecap="round" opacity="0.8" />
          <path d="M 58 42 Q 72 41, 86 42" stroke="#334155" strokeWidth="1.2" strokeLinecap="round" opacity="0.8" />
          <path d="M 58 70 Q 72 69, 86 70" stroke="#334155" strokeWidth="1.2" strokeLinecap="round" opacity="0.8" />

          {/* Center Spine Fold */}
          <line x1="48" y1="16" x2="48" y2="82" stroke="#cbd5e1" strokeWidth="1.5" />

          {/* Page Turn Animation */}
          <g className="animate-page-turn">
            <rect x="48" y="16" width="46" height="66" rx="4" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.5" />
            <line x1="48" y1="30" x2="94" y2="30" stroke="#bfdbfe" strokeWidth="0.75" />
            <line x1="48" y1="44" x2="94" y2="44" stroke="#bfdbfe" strokeWidth="0.75" />
          </g>

          {/* Dynamic Written Ink Line */}
          <path
            d="M 58 49 Q 68 48, 78 49"
            stroke="#1d4ed8"
            strokeWidth="2"
            strokeLinecap="round"
            className="animate-ink-trace"
          />
        </g>

        {/* ---------------- 2. CLOSED STATE (Matches Image 1) ---------------- */}
        <g className="animate-closed-book">
          {/* Subtle Outer Drop Shadow */}
          <rect x="10" y="10" width="80" height="80" rx="6" fill="#020617" opacity="0.1" transform="rotate(-3 50 50)" />

          {/* Clean Rounded Closed Dark Blue Notebook Cover */}
          <rect
            x="10"
            y="10"
            width="80"
            height="80"
            rx="6"
            fill="url(#navyCoverGrad)"
            transform="rotate(-3 50 50)"
          />

          {/* Left Paper Stack Edge */}
          <path d="M 6 14 C 6 12, 7 11, 8 11 L 11 11 L 7 88 L 4 88 Z" fill="#e2e8f0" opacity="0.9" />

          {/* Center "NOTEBOOK" Badge Label (Matching Image 1!) */}
          <g transform="rotate(-3 50 50)">
            <rect
              x="26"
              y="42"
              width="48"
              height="16"
              rx="3"
              fill="none"
              stroke="#60a5fa"
              strokeWidth="1.2"
              strokeOpacity="0.6"
            />
            <text
              x="50"
              y="52.5"
              fill="#ffffff"
              fontSize="6"
              fontWeight="900"
              fontFamily="sans-serif"
              textAnchor="middle"
              letterSpacing="1.5"
              opacity="0.95"
            >
              NOTEBOOK
            </text>
          </g>
        </g>

        {/* ---------------- 3. SLEEK 3D PENCIL (Exact match to reference images) ---------------- */}
        <g className="animate-pencil-story">
          {/* Eraser Dome Cap */}
          <path
            d="M 61 14 C 63 12, 66 12, 68 14 C 70 16, 70 19, 68 21 L 63.5 25.5 L 56.5 18.5 Z"
            fill="#f43f5e"
          />
          {/* Metallic Silver Ferrule Collar */}
          <path d="M 56.5 18.5 L 63.5 25.5 L 59.5 29.5 L 52.5 22.5 Z" fill="#cbd5e1" />
          <line x1="55" y1="20" x2="62" y2="27" stroke="#64748b" strokeWidth="0.8" />
          <line x1="56.5" y1="21.5" x2="63.5" y2="28.5" stroke="#ffffff" strokeWidth="0.8" opacity="0.9" />

          {/* Slender 3D Yellow Pencil Barrel */}
          <path d="M 52.5 22.5 L 59.5 29.5 L 36.5 52.5 L 29.5 45.5 Z" fill="url(#pencilYellowBodyGrad)" />
          {/* Glossy White Lengthwise Highlight Reflection */}
          <line x1="48" y1="24.5" x2="33" y2="39.5" stroke="#ffffff" strokeWidth="1" opacity="0.65" strokeLinecap="round" />
          {/* 3D Curved Shadow Edge */}
          <path d="M 47.5 27.5 L 54.5 34.5 L 36.5 52.5 L 29.5 45.5 Z" fill="#b45309" opacity="0.25" />

          {/* Sharpened Wood Cone Tip */}
          <path d="M 29.5 45.5 L 36.5 52.5 L 28 54 Z" fill="#fef08a" />

          {/* Dark Graphite Lead Point */}
          <path d="M 29.8 53.4 L 28 54 L 28.6 52.2 Z" fill="#0f172a" />
        </g>
      </svg>
    </div>
  );
}

export interface LoadingBlockProps {
  /** Announced to screen readers; rendered below loader if present. */
  label?: string;
  /** Fills the viewport — for a whole screen that has nothing on it yet. */
  fullPage?: boolean;
  className?: string;
}

/**
 * A centred loader block for screens, panels, or table bodies.
 */
export function LoadingBlock({
  label = 'Loading…',
  fullPage = false,
  className,
}: LoadingBlockProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-slate-600 dark:text-slate-300',
        fullPage ? 'min-h-dvh bg-paper' : 'py-12',
        className,
      )}
    >
      <Loader size={fullPage ? 'xl' : 'lg'} />
      {label && (
        <span className="text-xs font-extrabold tracking-wide text-slate-500 dark:text-slate-400 animate-pulse">
          {label}
        </span>
      )}
    </div>
  );
}
